import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BottomTabBar from '../components/BottomTabBar';
import FanTabBar from '../components/FanTabBar';
import LiveStatusFloat from '../components/LiveStatusFloat';
import ProfilePage from '../pages/ProfilePage';
import { useProfileDrawer } from '../lib/profileDrawer';

const Layout: React.FC = () => {
  const { pathname } = useLocation();
  const { user, isAuthenticated } = useContext(AuthContext);
  // Same "in the app" test the tab bar uses for its own visibility.
  const inApp = isAuthenticated && !!user;

  // ── Profile drawer (Fanvue-style push panel) ──────────────────
  // "#profile" in the URL requests the drawer; a 3-phase state machine gives
  // the close a slide-out (the hash is already gone while "closing", so the
  // panel must outlive it by one animation).
  const { isOpen: drawerRequested } = useProfileDrawer();
  const [drawerPhase, setDrawerPhase] = useState<'closed' | 'open' | 'closing'>(
    drawerRequested ? 'open' : 'closed'
  );
  useEffect(() => {
    if (drawerRequested) setDrawerPhase('open');
    else setDrawerPhase((p) => (p === 'open' ? 'closing' : p));
  }, [drawerRequested]);
  // Safety net: if the exit animation's end event never arrives (tab
  // backgrounded mid-close), force the unmount.
  useEffect(() => {
    if (drawerPhase !== 'closing') return;
    const t = setTimeout(() => setDrawerPhase('closed'), 450);
    return () => clearTimeout(t);
  }, [drawerPhase]);

  const drawerOpen = inApp && drawerPhase === 'open';

  // While open: body class drives the push transforms (page + fixed bars all
  // slide left together, see ProfilePage.css), and the body is pinned with
  // the BottomSheet recipe so the tab underneath keeps its exact scroll spot
  // ("return right where I left it"). Both release at closing-start so the
  // shell slides back in sync with the panel's exit.
  useEffect(() => {
    if (!drawerOpen) return;
    document.body.classList.add('profile-drawer-open');
    const scrollY = window.scrollY;
    const { style } = document.body;
    const prev = {
      position: style.position,
      top: style.top,
      width: style.width,
      overflow: style.overflow,
    };
    style.position = 'fixed';
    style.top = `-${scrollY}px`;
    style.width = '100%';
    style.overflow = 'hidden';
    return () => {
      document.body.classList.remove('profile-drawer-open');
      style.position = prev.position;
      style.top = prev.top;
      style.width = prev.width;
      style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [drawerOpen]);

  // Hide the site footer on the authed bottom-tab-bar app screens. The footer's
  // links (Terms/Privacy/About) are reachable from those screens' own menus, and
  // on mobile the footer otherwise sits dead below the fixed tab bar — making an
  // otherwise one-screen page scrollable (phantom scroll = iOS can drag the
  // fixed header under the status bar). Terms/Privacy are also public pages:
  // logged-out visitors have no tab bar, so they keep the footer.
  const hideFooter =
    pathname === "/requests" ||
    pathname === "/payout-history" ||
    pathname === "/balance" ||
    pathname === "/leaderboard" ||
    pathname === "/finish-stripe" ||
    pathname === "/stripe-rejected" ||
    pathname.startsWith("/qr/") ||
    pathname.startsWith("/disputes/") ||
    pathname.startsWith("/claims/") ||
    // App fan mode tab pages — one-screen surfaces with their own bar; a
    // footer below the fixed bar would only add phantom scroll. /fan/login
    // (the web magic-link landing) keeps its footer as shipped.
    pathname === "/fan" ||
    pathname === "/fan/requests" ||
    pathname === "/fan/leaderboard" ||
    (inApp && (pathname === "/terms" || pathname === "/privacy"));

  return (
    <>
      <Header />
      {/* Header is position:fixed (see Header.tsx) so it can never scroll out
          of view — it no longer reserves space in normal flow. --app-header-h
          is measured live off the real header DOM node, so this stays correct
          across breakpoints and safe-area changes without duplicating either
          here. --app-tabbar-h is the mirror for the fixed bottom tab bar
          (0px whenever the bar self-hides). */}
      <main
        className="app-main"
        style={{
          // Exactly fill the gap between the fixed header and tab bar, no more:
          // the old '80vh' overshot that gap on phones (80vh > 100dvh minus the
          // bars), leaving 25-60px of phantom scroll on the pinned one-screen
          // pages (QR, Balance) — the "why does this still scroll" bug,
          // 1.1(26) prep. Content taller than one screen still grows main and
          // scrolls normally. dvh tracks the live iOS viewport; on engines
          // without dvh the declaration is dropped and main just hugs content.
          minHeight: 'calc(var(--app-bar-top, 100dvh - var(--app-tabbar-h, 0px)) - var(--app-header-h, 0px))',
          paddingTop: 'var(--app-header-h, 0px)',
          paddingBottom: 'var(--app-tabbar-h, 0px)',
        }}
      >
        <Outlet />
      </main>
      {!hideFooter && <Footer />}
      <LiveStatusFloat />
      <BottomTabBar />
      {/* Fan bar mounts after the DJ bar so its --app-tabbar-h write wins
          within the same commit on the routes where it shows (the two are
          never visible together — disjoint route sets). */}
      <FanTabBar />
      {/* The drawer mounts LAST (paint order) and OUTSIDE <main> (the pushed
          shell) so the push transforms never become its containing block. The
          wrapper div is class-less while open — no stacking context, so the
          panel's no-z-index contract from ProfilePage.css still holds. */}
      {inApp && drawerPhase !== 'closed' && (
        <div
          className={
            drawerPhase === 'closing'
              ? 'profile-drawer-root profile-drawer-exit'
              : 'profile-drawer-root'
          }
          onAnimationEnd={(e) => {
            if (e.animationName === 'profile-panel-out') setDrawerPhase('closed');
          }}
        >
          <ProfilePage />
        </div>
      )}
    </>
  );
};

export default Layout;
